import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import selectIsPurchaserFormValid from 'utils/purchase/selectIsPurchaserFormValid';
import './WalletButtonContainer.scss';

/**
 * Shared container for an express-wallet native button (Adyen component / Stripe ECE) with a
 * click-blocking overlay for purchaser-form validation. Used by Google Pay and Apple Pay alike.
 *
 * The native wallet sheet opens on the click gesture and cannot be reliably stopped afterwards:
 * Adyen's onClick `reject()` does not prevent it, and the Stripe ECE briefly opens then aborts
 * (visible flicker). So when the purchaser form is invalid we overlay a transparent button that
 * intercepts the click before it reaches the native button, and route it to the gate (without
 * resolve/reject) so it surfaces the form errors instead of opening the sheet. Once the form is
 * valid the overlay is removed and clicks reach the real button.
 *
 * The native button is mounted into the inner container via the forwarded ref.
 *
 * @param {Object} props
 * @param {Function} props.onPaymentButtonClick - Checkout gate; called with `{}` from the overlay.
 * @param {boolean} [props.quickCheckoutAgreementsBlocked] - Optional Quick Purchase legal overlay.
 * @param {boolean} props.hidden - Hide the container (loading / total recalculating / unavailable).
 * @param {string} props.label - Accessible label for the overlay button (wallet-specific).
 * @param {string} [props.containerClassName] - Extra class for engine-specific container styles.
 */
const WalletButtonContainer = React.forwardRef(
  (
    {
      onPaymentButtonClick,
      hidden,
      label,
      containerClassName,
      quickCheckoutAgreementsBlocked = false,
    },
    ref,
  ) => {
    const showPurchaserForm = useSelector(
      (state) => state.whitelabelConfig?.features?.PURCHASER_FORM_ON === 'CHECKOUT',
    );
    const isPurchaserFormValid = useSelector(selectIsPurchaserFormValid);
    const blockButton =
      quickCheckoutAgreementsBlocked || (showPurchaserForm && !isPurchaserFormValid);

    return (
      <div className="wallet-button-wrapper" style={{ display: hidden ? 'none' : 'block' }}>
        {blockButton && (
          <button
            type="button"
            className="wallet-button-overlay"
            aria-label={label}
            onClick={() => onPaymentButtonClick({})}
          />
        )}
        <div className={`wallet-button-container ${containerClassName || ''}`.trim()} ref={ref} />
      </div>
    );
  },
);

WalletButtonContainer.displayName = 'WalletButtonContainer';

WalletButtonContainer.propTypes = {
  onPaymentButtonClick: PropTypes.func.isRequired,
  hidden: PropTypes.bool,
  quickCheckoutAgreementsBlocked: PropTypes.bool,
  label: PropTypes.string.isRequired,
  containerClassName: PropTypes.string,
};

export default WalletButtonContainer;
